// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const facebookShareLink = (pageLink: string) => `https://www.facebook.com/sharer/sharer.php?u=${pageLink}`;

const linkedinShareLink = (pageLink: string) => `https://www.linkedin.com/sharing/share-offsite/?url=${pageLink}`;

const browserEmailUrl = (pageLink: string, pageTitle: string) =>
    `mailto:user@example.com?subject=${pageTitle}t&body=${pageLink}`;

const mapStateToProps = (state: ApplicationState) => ({
    page: getCurrentPage(state),
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasToasterContext & HasShellContext;

export interface NewsPageViewStyles {
    authorText?: TypographyProps;
    publishDateText?: TypographyProps;
    newsWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    dateAndAuthorWrapper?: InjectableCss;
    pageTitle?: TypographyProps;
    shareLinks?: InjectableCss;
    copyLink?: LinkPresentationProps;
    mailToLink?: LinkPresentationProps;
    printLink?: LinkPresentationProps;
    fbShareLink?: LinkPresentationProps;
    linkedinShareLink?: LinkPresentationProps;
}

const generateShareLinkStyles = (icon: string, size: number, margin: number) => {
    return {
        icon: {
            iconProps: {
                src: icon,
                size,
            },
        },
        css: css`
            margin-left: ${margin}px;
            ${IconWrapper} {
                margin: 0;
            }
        `,
    } as LinkPresentationProps;
};

export const newsPageViewStyles: NewsPageViewStyles = {
    pageTitle: {
        variant: "h1",
        css: css`
            margin-bottom: 24px;
        `,
    },
    newsWrapper: {},
    contentWrapper: {
        css: css`
            margin-top: 8px;
        `,
    },
    dateAndAuthorWrapper: {
        css: css`
            display: flex;
            align-items: center;
            padding-bottom: 15px;
            border-bottom: 1px solid ${getColor("common.ui6")};
            margin-bottom: 23px;
            @media (max-width: 481px) {
                flex-wrap: wrap;
            }
        `,
    },
    authorText: {
        size: "12px",
        lineHeight: "18px",
    },
    publishDateText: {
        size: "12px",
        lineHeight: "18px",
    },
    shareLinks: {
        css: css`
            margin-left: auto;
            display: flex;
            align-items: center;
            font-size: 12px;
            line-height: 18px;
            @media (max-width: 481px) {
                width: 100%;
                margin-top: 16px;
            }
        `,
    },
    copyLink: generateShareLinkStyles("IconShareLink", 20, 16),
    mailToLink: generateShareLinkStyles("IconShareEmail", 16, 12),
    printLink: generateShareLinkStyles("IconSharePrint", 16, 12),
    fbShareLink: generateShareLinkStyles("IconShareFb", 16, 12),
    linkedinShareLink: generateShareLinkStyles("IconShareLinkedin", 16, 12),
};

const styles = newsPageViewStyles;

const NewsPageView = ({
    page,
    fields: { viaFb, viaLi, showPrint, showMailTo, showCopyLink },
    toaster,
    shellContext,
}: Props) => {
    if (!page) {
        return null;
    }

    const copyLinkToClipboard = () => {
        if (typeof document === "undefined" || document === null || typeof window === "undefined" || window === null) {
            return;
        }
        const dummy = document.createElement("input");
        const text = window.location.href;

        document.body.appendChild(dummy);
        dummy.value = text;
        dummy.select();
        document.execCommand("copy");
        document.body.removeChild(dummy);
        toaster.addToast({
            body: translate("Link Copied!"),
            messageType: "info",
        });
    };

    return (
        <StyledWrapper {...styles.newsWrapper} key={page.id}>
            <Typography as="h1" {...styles.pageTitle}>
                {page.fields["title"]}
            </Typography>
            <StyledWrapper {...styles.dateAndAuthorWrapper}>
                {page.fields["author"] && (
                    <Typography {...styles.authorText} data-test-selector="newslist_author">
                        {translate("By")}&nbsp;{page.fields["author"]}
                    </Typography>
                )}
                &nbsp;|&nbsp;
                {page.fields["publishDate"] && (
                    <Typography {...styles.publishDateText} data-test-selector="newslist_publishdate">
                        <LocalizedDateTime
                            dateTime={new Date(page.fields["publishDate"])}
                            options={{ year: "numeric", month: "long", day: "numeric" }}
                        />
                    </Typography>
                )}
                {(viaFb || viaLi || showPrint || showMailTo || showCopyLink) && (
                    <StyledWrapper {...styles.shareLinks}>
                        {translate("Share")}
                        {showCopyLink && <Link onClick={copyLinkToClipboard} {...styles.copyLink} />}
                        {showMailTo && (
                            <Link
                                href={`mailTo:${browserEmailUrl(
                                    shellContext.isEditing ? "" : window.location.href,
                                    page.fields["title"],
                                )}`}
                                {...styles.mailToLink}
                            />
                        )}
                        {showPrint && <Link onClick={() => window.print()} {...styles.printLink} />}
                        {viaFb && (
                            <Link
                                target="_blank"
                                href={facebookShareLink(shellContext.isEditing ? "" : window.location.href)}
                                {...styles.fbShareLink}
                            />
                        )}
                        {viaLi && (
                            <Link
                                target="_blank"
                                href={linkedinShareLink(shellContext.isEditing ? "" : window.location.href)}
                                {...styles.linkedinShareLink}
                            />
                        )}
                    </StyledWrapper>
                )}
            </StyledWrapper>
            {(page.fields["newsSummary"] || page.fields["newsContent"]) && (
                <StyledWrapper {...styles.contentWrapper} data-test-selector="newslist_content">
                    {parse(page.fields["newsContent"], parserOptions)}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withToaster(withIsInShell(NewsPageView))),
    definition: {
        group: "News Page",
        allowedContexts: ["NewsPage"],
        fieldDefinitions: [
            {
                name: "viaFb",
                displayName: "Share Via Facebook",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: "viaLi",
                displayName: "Share Via Linked",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: "showPrint",
                displayName: "Show Print Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: "showMailTo",
                displayName: "Show Mail-to Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: "showCopyLink",
                displayName: "Show Copy Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
